import { logger } from 'common/services';
import { UrlNames } from 'modules/app/constants';
import { configService } from 'modules/app/services';

import { WEBSOCKET_CONFIG } from './constants/websocket';
import { OpenNatterSocket, type OpenNatterSocketConfig } from './socket/OpenNatterSocket';

/**
 * Open Natter Feature Module
 *
 * Provides lazy initialization for Open Natter services.
 * Services are only created when the feature is actually used,
 * preventing crashes when environment variables are missing.
 *
 * Usage:
 * ```typescript
 * // In component or hook
 * const socket = openNatterFeature.getSocket();
 * const apiUrl = openNatterFeature.getApiUrl();
 * ```
 */
class OpenNatterFeature {
  private socket: OpenNatterSocket | null = null;
  private initialized = false;

  /**
   * Initialize the feature
   * Validates environment variables and creates services
   * Throws if required configuration is missing
   */
  initialize(): void {
    if (this.initialized) return;

    // Validate required environment variables
    const apiUrl = configService.getUrl(UrlNames.OpenNatterApi);
    const wsUrl = configService.getUrl(UrlNames.OpenNatterWs);

    if (!apiUrl || !wsUrl) {
      throw new Error('Open Natter feature requires VITE_APP_OPEN_NATTER_API_URL and VITE_APP_OPEN_NATTER_WS_URL');
    }

    logger.info('[OpenNatterFeature] Initialized', { apiUrl, wsUrl });
    this.initialized = true;
  }

  /**
   * Check if the feature is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Get the WebSocket client (lazy creation)
   */
  getSocket(): OpenNatterSocket {
    if (!this.socket) {
      const config: OpenNatterSocketConfig = {
        wsUrl: configService.getUrl(UrlNames.OpenNatterWs),
        reconnectDelay: WEBSOCKET_CONFIG.RECONNECT_DELAY_MS,
        heartbeatIncoming: WEBSOCKET_CONFIG.HEARTBEAT_INCOMING_MS,
        heartbeatOutgoing: WEBSOCKET_CONFIG.HEARTBEAT_OUTGOING_MS,
        maxReconnectAttempts: WEBSOCKET_CONFIG.MAX_RECONNECT_ATTEMPTS,
      };
      this.socket = new OpenNatterSocket(config);
    }
    return this.socket;
  }

  /**
   * Get the API base URL
   */
  getApiUrl(): string {
    return configService.getUrl(UrlNames.OpenNatterApi);
  }
}

/**
 * Singleton instance
 */
export const openNatterFeature = new OpenNatterFeature();
